import React, { useState, useMemo } from "react";

interface CMANetSheetCalculatorProps {
  agentPriceLow: number | null;
  agentPriceHigh: number | null;
  suggestedPriceLow: number | null;
  suggestedPriceHigh: number | null;
  mortgagePayoff: number | null;
  commissionPct: number | null;
  closingCostPct: number | null;
  sellerCredits: number | null;
  otherFees: number | null;
  primaryColor: string;
}

function fmt(n: number) {
  return "$" + Math.round(n).toLocaleString();
}

export function CMANetSheetCalculator({
  agentPriceLow,
  agentPriceHigh,
  suggestedPriceLow,
  suggestedPriceHigh,
  mortgagePayoff,
  commissionPct,
  closingCostPct,
  sellerCredits,
  otherFees,
  primaryColor,
}: CMANetSheetCalculatorProps) {
  // Determine price range for slider
  const priceLow = agentPriceLow ?? suggestedPriceLow;
  const priceHigh = agentPriceHigh ?? suggestedPriceHigh;

  const midpoint = useMemo(() => {
    if (priceLow && priceHigh) return Math.round(((priceLow + priceHigh) / 2) / 5000) * 5000;
    if (priceLow) return priceLow;
    if (priceHigh) return priceHigh;
    return 500_000;
  }, [priceLow, priceHigh]);

  const sliderMin = useMemo(() => Math.round((midpoint * 0.8) / 5000) * 5000, [midpoint]);
  const sliderMax = useMemo(() => Math.round((midpoint * 1.2) / 5000) * 5000, [midpoint]);

  const [sliderPrice, setSliderPrice] = useState(midpoint);

  // Return null if none of the net sheet inputs are set
  if (
    mortgagePayoff == null &&
    commissionPct == null &&
    closingCostPct == null &&
    sellerCredits == null &&
    otherFees == null
  ) {
    return null;
  }

  const commission = commissionPct != null ? (sliderPrice * commissionPct) / 100 : 0;
  const closingCosts = closingCostPct != null ? (sliderPrice * closingCostPct) / 100 : 0;
  const payoff = mortgagePayoff ?? 0;
  const credits = sellerCredits ?? 0;
  const fees = otherFees ?? 0;
  const totalDeductions = payoff + commission + closingCosts + credits + fees;
  const netProceeds = sliderPrice - totalDeductions;

  const sliderPct =
    sliderMax > sliderMin
      ? ((sliderPrice - sliderMin) / (sliderMax - sliderMin)) * 100
      : 50;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 md:p-8">
      {/* Slider */}
      <div className="mb-8">
        <div className="flex items-baseline justify-between mb-2">
          <p className="text-sm text-slate-500">Sale Price</p>
          <p className="text-2xl font-bold text-slate-900">{fmt(sliderPrice)}</p>
        </div>
        <input
          type="range"
          min={sliderMin}
          max={sliderMax}
          step={5000}
          value={sliderPrice}
          onChange={(e) => setSliderPrice(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${sliderPct}%, #e2e8f0 ${sliderPct}%, #e2e8f0 100%)`,
            accentColor: primaryColor,
          }}
        />
        <div className="flex justify-between text-xs text-slate-400 mt-1">
          <span>{fmt(sliderMin)}</span>
          <span>{fmt(sliderMax)}</span>
        </div>
      </div>

      {/* Breakdown table */}
      <div className="space-y-2 text-sm">
        <div className="flex justify-between text-slate-700">
          <span>Sale Price</span>
          <span className="font-medium">{fmt(sliderPrice)}</span>
        </div>

        {mortgagePayoff != null && (
          <div className="flex justify-between text-slate-500">
            <span>– Mortgage Payoff</span>
            <span>{fmt(mortgagePayoff)}</span>
          </div>
        )}

        {commissionPct != null && (
          <div className="flex justify-between text-slate-500">
            <span>– Commission ({commissionPct}%)</span>
            <span>{fmt(commission)}</span>
          </div>
        )}

        {closingCostPct != null && (
          <div className="flex justify-between text-slate-500">
            <span>– Closing Costs ({closingCostPct}%)</span>
            <span>{fmt(closingCosts)}</span>
          </div>
        )}

        {sellerCredits != null && sellerCredits > 0 && (
          <div className="flex justify-between text-slate-500">
            <span>– Seller Credits</span>
            <span>{fmt(sellerCredits)}</span>
          </div>
        )}

        {otherFees != null && otherFees > 0 && (
          <div className="flex justify-between text-slate-500">
            <span>– Other Fees</span>
            <span>{fmt(otherFees)}</span>
          </div>
        )}

        <div className="border-t border-slate-200 my-2" />

        <div
          className="flex justify-between text-base font-bold"
          style={{ color: netProceeds < 0 ? "#b45309" : primaryColor }}
        >
          <span>Net Proceeds</span>
          <span>{netProceeds < 0 ? `–$${Math.round(Math.abs(netProceeds)).toLocaleString()}` : fmt(netProceeds)}</span>
        </div>

        {netProceeds < 0 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
            At this sale price, estimated deductions exceed proceeds. Consider a higher list price or review fee inputs.
          </p>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-4">
        Estimate only. Actual proceeds may vary based on final negotiations, prorations, and lender requirements.
      </p>
    </div>
  );
}
